import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { walletAPI, transactionsAPI } from '../services/api';
import { bizWorkspaceAPI } from '../services/bizApi';
import PageShell from '../components/PageShell';
import {
  Wallet, ShieldCheck, Receipt, UsersThree, ChartBar,
  User, NotePencil, Lock, Question, HandHeart, Certificate,
  Lightning, Flask, ArrowUpRight, TrendUp, ClockCounterClockwise,
} from '@phosphor-icons/react';

const formatCurrency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

/* -------- Hero metric card -------- */
function MetricCard({ label, value, hint, icon: Icon, tone = 'neutral', testId, to }) {
  const { t } = useTheme();
  const toneClr =
    tone === 'positive' ? t.accent :
    tone === 'warn' ? '#F59E0B' : t.textPrimary;

  const inner = (
    <div
      className="relative overflow-hidden rounded-2xl p-5 h-full transition-all duration-200"
      style={{
        background: t.surface,
        border: `1px solid ${t.borderSubtle}`,
        boxShadow: 'none',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = t.borderHover;
        e.currentTarget.style.boxShadow = t.hoverGlow;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = t.borderSubtle;
        e.currentTarget.style.boxShadow = 'none';
      }}
      data-testid={testId}
    >
      <div className="flex items-center gap-2.5 mb-3">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: t.accentSoft }}
        >
          <Icon className="w-4 h-4" weight="bold" style={{ color: toneClr }} />
        </div>
        <p className="text-[11px] tracking-[0.14em] uppercase font-semibold" style={{ color: t.textFaint }}>{label}</p>
      </div>
      <p
        className="text-3xl sm:text-[32px] font-black tracking-tight leading-none tabular-nums"
        style={{ fontFamily: 'Cabinet Grotesk', color: t.textPrimary }}
      >
        {value}
      </p>
      {hint && <p className="text-xs mt-2" style={{ color: t.textMutedNew }}>{hint}</p>}
      {to && <ArrowUpRight className="w-4 h-4 absolute top-4 right-4" weight="bold" style={{ color: t.textFaint }} />}
    </div>
  );
  return to ? <Link to={to} className="block h-full">{inner}</Link> : inner;
}

/* -------- Categorized item -------- */
function GroupItem({ to, label, hint, icon: Icon, testId, accent = false }) {
  const { t, isDark } = useTheme();
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group flex items-center gap-3 p-3.5 rounded-xl transition-all duration-150"
      style={{
        background: t.surfaceAlt,
        border: `1px solid ${t.dividerBorder}`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = t.borderHover;
        e.currentTarget.style.background = t.surfaceHover;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = t.dividerBorder;
        e.currentTarget.style.background = t.surfaceAlt;
      }}
    >
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105"
        style={{ background: t.accentSoft }}
      >
        <Icon className="w-5 h-5" weight="bold" style={{ color: accent ? t.accent : (isDark ? '#ffffff' : '#0F172A') }} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold truncate" style={{ color: t.textPrimary }}>{label}</p>
        {hint && <p className="text-[11px] truncate mt-0.5" style={{ color: t.textMutedNew }}>{hint}</p>}
      </div>
      <span className="text-lg leading-none transition-colors" style={{ color: t.textFaint }}>›</span>
    </Link>
  );
}

/* -------- Group section wrapper -------- */
function GroupSection({ title, children, testId }) {
  const { t } = useTheme();
  return (
    <section className="mb-6" data-testid={testId}>
      <h2 className="text-[11px] tracking-[0.16em] uppercase font-bold mb-3 px-1" style={{ color: t.accentText }}>
        {title}
      </h2>
      <div
        className="rounded-2xl p-3 space-y-2"
        style={{ background: t.surface, border: `1px solid ${t.borderSubtle}` }}
      >
        {children}
      </div>
    </section>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { t } = useTheme();
  const [balance, setBalance] = useState(0);
  const [invoiceStats, setInvoiceStats] = useState({ outstandingCount: 0, outstandingAmount: 0 });
  const [volume30d, setVolume30d] = useState(0);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    walletAPI.getBalance()
      .then((res) => setBalance(res.data.balance || 0))
      .catch((err) => {
        console.error('Dashboard: failed to load balance:', err);
        setLoadError('Some dashboard data failed to load. Please refresh the page.');
      });

    bizWorkspaceAPI.listInvoices()
      .then((res) => {
        const list = res.data?.invoices || [];
        const outstanding = list.filter((i) => (i.status || '').toLowerCase() !== 'paid');
        const amt = outstanding.reduce((sum, i) => sum + (i.amount_due || i.amount || 0), 0);
        setInvoiceStats({ outstandingCount: outstanding.length, outstandingAmount: amt });
      })
      .catch(() => { /* not onboarded / no biz — silent */ });

    if (transactionsAPI?.getHistory) {
      transactionsAPI.getHistory(100)
        .then((res) => {
          const txns = res.data?.transactions || [];
          const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
          const vol = txns
            .filter((x) => new Date(x.created_at).getTime() > cutoff && x.type !== 'platform_fee')
            .reduce((s, x) => s + (x.amount || 0), 0);
          setVolume30d(vol);
        })
        .catch(() => { /* silent */ });
    }
  }, []);

  const firstName = user?.name?.split(' ')[0] || user?.username || 'there';
  const isAdmin = user?.role === 'admin';

  return (
    <PageShell testId="dashboard-page">
      {/* Hero header */}
      <div className="mb-6" data-testid="dashboard-hero-header">
        <p className="text-[11px] tracking-[0.18em] uppercase font-semibold mb-2" style={{ color: t.textFaint }}>
          Dashboard Overview
        </p>
        <h1
          className="text-3xl sm:text-4xl font-black tracking-tight leading-tight"
          style={{ fontFamily: 'Cabinet Grotesk', color: t.textPrimary }}
        >
          Welcome back, {firstName}.
        </h1>
        <p className="text-sm mt-1.5" style={{ color: t.textMutedNew }}>
          Manage payments, invoices, splits and account controls in one place.
        </p>
      </div>

      {/* Data-load error state — shown instead of a blank/broken dashboard */}
      {loadError && (
        <div
          className="mb-6 rounded-xl px-4 py-3 text-sm font-medium"
          style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444' }}
          data-testid="dashboard-load-error"
        >
          {loadError}
        </div>
      )}

      {/* Hero metric row */}
      <section
        className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-8"
        data-testid="dashboard-metrics"
      >
        <MetricCard
          label="Wallet Balance"
          value={formatCurrency(balance)}
          hint="Available cash"
          icon={Wallet}
          tone="positive"
          testId="metric-balance"
          to="/wallet"
        />
        <MetricCard
          label="30-day Volume"
          value={formatCurrency(volume30d)}
          hint="Money moved last 30 days"
          icon={TrendUp}
          tone="neutral"
          testId="metric-volume"
          to="/history"
        />
        <MetricCard
          label="Outstanding Invoices"
          value={
            invoiceStats.outstandingCount === 0
              ? '—'
              : `${invoiceStats.outstandingCount} · ${formatCurrency(invoiceStats.outstandingAmount)}`
          }
          hint={
            invoiceStats.outstandingCount === 0
              ? 'No invoices awaiting payment'
              : 'Client payments pending'
          }
          icon={Receipt}
          tone={invoiceStats.outstandingCount > 0 ? 'warn' : 'neutral'}
          testId="metric-invoices"
          to="/biz/invoices"
        />
      </section>

      <GroupSection title="Core Finance" testId="group-core-finance">
        <GroupItem to="/wallet" label="Wallet" hint="Balances & payouts" icon={Wallet} accent testId="group-wallet" />
        <GroupItem to="/biz/setup" label="Payments Setup" hint="Stripe Connect verification" icon={ShieldCheck} accent testId="group-biz-setup" />
        <GroupItem to="/biz/invoices" label="Invoices" hint="Bill clients via Stripe" icon={Receipt} accent testId="group-biz-invoices" />
        <GroupItem to="/biz/splits" label="Splits" hint="Revenue sharing with collaborators" icon={UsersThree} accent testId="group-biz-splits" />
        <GroupItem to="/dashboard/unified" label="Unified Financials" hint="Cash, activity & P&L" icon={ChartBar} accent testId="group-unified" />
      </GroupSection>

      <GroupSection title="Account Management" testId="group-account">
        <GroupItem to="/profile" label="Account" hint="Your public profile" icon={User} testId="group-account-profile" />
        <GroupItem to="/settings" label="Edit Profile" hint="Name, avatar, contact" icon={NotePencil} testId="group-account-edit" />
        <GroupItem to="/settings/privacy" label="Privacy & GDPR" hint="Data controls" icon={Lock} testId="group-account-privacy" />
      </GroupSection>

      <GroupSection title="Support" testId="group-support">
        <GroupItem to="/feedback" label="Help & Feedback" hint="Get support" icon={Question} testId="group-support-help" />
        <GroupItem to="/biz/pledges" label="Support Pages" hint="Let backers fund your projects" icon={HandHeart} testId="group-support-pledges" />
        <GroupItem to="/settings/2fa" label="Security & SOC 2 Status" hint="2FA and compliance" icon={Certificate} testId="group-support-security" />
      </GroupSection>

      {isAdmin && (
        <GroupSection title="Admin" testId="group-admin">
          <GroupItem to="/admin/waitlist" label="Waitlist" hint="Manage invites & leads" icon={UsersThree} accent testId="group-admin-waitlist" />
          <GroupItem to="/admin/features" label="Feature Phases" hint="Feature flags rollout" icon={Lightning} accent testId="group-admin-phases" />
          <GroupItem to="/admin/beta" label="Beta Users" hint="Invite code management" icon={Flask} testId="group-admin-beta" />
          <GroupItem to="/admin/analytics" label="Analytics" hint="Fees & platform metrics" icon={ChartBar} accent testId="group-admin-analytics" />
        </GroupSection>
      )}

      <div className="mt-2">
        <Link
          to="/history"
          className="inline-flex items-center gap-2 text-xs transition-colors hover:opacity-80"
          style={{ color: t.textMutedNew }}
          data-testid="dashboard-view-history"
        >
          <ClockCounterClockwise className="w-4 h-4" />
          View transaction history
        </Link>
      </div>
    </PageShell>
  );
}
